import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { AppDispatch } from 'store'
import { createNewPlan } from './operationsSlice'
import { Priority, TargetMetricType, HorizonType, DegradeRule } from './types/operations.types'
import {
  OpsPageWrapper,
  OpsCard,
  OpsCardHeader,
  StepperWrapper,
  StepItem,
  StepCircle,
  StepLabel,
  StepConnector,
  FormSection,
  FormLabel,
  FormInput,
  FormTextarea,
  FormSelect,
  RadioGroup,
  RadioCard,
  ReviewSection,
  ReviewLabel,
  ReviewValue,
  InfoBox,
  SuccessScreen,
  OpsButton,
  Badge,
  opsColors,
} from './styled/Operations.styled'

const STEPS = ['Plan Identity', 'Operational Target', 'Cycle Templates', 'Horizon Config', 'Review']
const PRIORITIES: Priority[] = ['Critical', 'High', 'Medium', 'Low']
const METRIC_TYPES: TargetMetricType[] = ['Tons', 'Cubic Meters', 'Square Meters', 'Count', 'Kilometers']
const METRIC_UNITS: Record<TargetMetricType, string> = {
  Tons: 't/h',
  'Cubic Meters': 'm³',
  'Square Meters': 'm²',
  Count: 'units',
  Kilometers: 'km',
}

export const CreateOperationPlan: React.FC = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch<AppDispatch>()
  const [step, setStep] = useState(0)
  const [created, setCreated] = useState(false)

  // Step 1
  const [planName, setPlanName] = useState('')
  const [siteId, setSiteId] = useState('')
  const [departmentId, setDepartmentId] = useState('')
  const [priority, setPriority] = useState<Priority>('Medium')
  const [notes, setNotes] = useState('')

  // Step 2
  const [metricType, setMetricType] = useState<TargetMetricType>('Tons')
  const [targetValue, setTargetValue] = useState('')
  const [dueDate, setDueDate] = useState('')

  // Step 3 - simplified (mock cycle templates)
  const [bindings, setBindings] = useState<Array<{ id: string; name: string; parallelism: number; priorityVal: number; degradeRule: DegradeRule }>>([])

  // Step 4
  const [horizonType, setHorizonType] = useState<HorizonType>('Next Hours')
  const [horizonValue, setHorizonValue] = useState('6')
  const [replenishThreshold, setReplenishThreshold] = useState('3')
  const [maxQueueSize, setMaxQueueSize] = useState('20')

  const handleCreate = async () => {
    await dispatch(createNewPlan({
      name: planName,
      siteId: Number(siteId),
      departmentId: Number(departmentId),
      priority,
      targetMetricType: metricType,
      targetValue: Number(targetValue),
      dueAt: dueDate || undefined,
      notes,
    }))
    setCreated(true)
  }

  const addMockBinding = () => {
    setBindings([...bindings, {
      id: `bind-${Date.now()}`,
      name: `Haul Cycle Template ${bindings.length + 1}`,
      parallelism: 3,
      priorityVal: 1,
      degradeRule: 'Pause',
    }])
  }

  if (created) {
    return (
      <OpsPageWrapper>
        <SuccessScreen>
          <div style={{ fontSize: '48px', color: opsColors.successBase }}>&#10003;</div>
          <h2>{planName}</h2>
          <Badge bg={opsColors.infoBase}>DRAFT</Badge>
          <p>Plan created successfully. No tasks will be generated until activation.</p>
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <OpsButton variant="secondary" onClick={() => { setCreated(false); setStep(0); setPlanName('') }}>Create Another</OpsButton>
            <OpsButton onClick={() => navigate('/ops-control/plans')}>Activate Plan</OpsButton>
          </div>
        </SuccessScreen>
      </OpsPageWrapper>
    )
  }

  return (
    <OpsPageWrapper>
      <h1 style={{ fontSize: '30px', fontWeight: 900, color: opsColors.textPrimary, margin: 0, fontFamily: 'DM Sans, sans-serif' }}>
        Create Operation Plan
      </h1>

      {/* Stepper */}
      <StepperWrapper>
        {STEPS.map((s, i) => (
          <React.Fragment key={s}>
            <StepItem>
              <StepCircle active={i === step} completed={i < step} onClick={() => i < step && setStep(i)} style={{ cursor: i < step ? 'pointer' : 'default' }}>
                {i < step ? '✓' : i + 1}
              </StepCircle>
              <StepLabel active={i === step}>{s}</StepLabel>
            </StepItem>
            {i < STEPS.length - 1 && <StepConnector />}
          </React.Fragment>
        ))}
      </StepperWrapper>

      <OpsCard>
        {/* Step 1: Plan Identity */}
        {step === 0 && (
          <FormSection>
            <OpsCardHeader><h3>Plan Identity</h3></OpsCardHeader>
            <div>
              <FormLabel>Plan Name *</FormLabel>
              <FormInput value={planName} onChange={(e) => setPlanName(e.target.value)} placeholder="e.g. Ore Haulage - Pit B to Crusher" />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div>
                <FormLabel>Site *</FormLabel>
                <FormSelect value={siteId} onChange={(e) => setSiteId(e.target.value)}>
                  <option value="">Select site...</option>
                  <option value="1">Site Alpha</option>
                  <option value="2">Site Beta</option>
                </FormSelect>
              </div>
              <div>
                <FormLabel>Department *</FormLabel>
                <FormSelect value={departmentId} onChange={(e) => setDepartmentId(e.target.value)}>
                  <option value="">Select department...</option>
                  <option value="1">Operations</option>
                  <option value="2">Mining</option>
                  <option value="3">Construction</option>
                  <option value="4">Maintenance</option>
                </FormSelect>
              </div>
            </div>
            <div>
              <FormLabel>Priority</FormLabel>
              <RadioGroup>
                {PRIORITIES.map((p) => (
                  <RadioCard key={p} active={priority === p} onClick={() => setPriority(p)}>{p}</RadioCard>
                ))}
              </RadioGroup>
            </div>
            <div>
              <FormLabel>Notes (optional)</FormLabel>
              <FormTextarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Additional context..." />
            </div>
          </FormSection>
        )}

        {/* Step 2: Operational Target */}
        {step === 1 && (
          <FormSection>
            <OpsCardHeader><h3>Operational Target</h3></OpsCardHeader>
            <div>
              <FormLabel>Target Metric Type</FormLabel>
              <RadioGroup>
                {METRIC_TYPES.map((m) => (
                  <RadioCard key={m} active={metricType === m} onClick={() => setMetricType(m)}>{m}</RadioCard>
                ))}
              </RadioGroup>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div>
                <FormLabel>Target Value *</FormLabel>
                <FormInput type="number" value={targetValue} onChange={(e) => setTargetValue(e.target.value)} placeholder="e.g. 240" />
              </div>
              <div>
                <FormLabel>Due Date (optional)</FormLabel>
                <FormInput type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
              </div>
            </div>
            {targetValue && (
              <InfoBox variant="info">
                Target: {targetValue} {METRIC_UNITS[metricType]} {dueDate ? `by ${new Date(dueDate).toLocaleDateString()}` : '(continuous)'}
              </InfoBox>
            )}
          </FormSection>
        )}

        {/* Step 3: Cycle Template Bindings */}
        {step === 2 && (
          <FormSection>
            <OpsCardHeader><h3>Cycle Template Bindings</h3></OpsCardHeader>
            <OpsButton variant="secondary" onClick={addMockBinding}>+ Add Cycle Template</OpsButton>
            {bindings.map((b, i) => (
              <OpsCard key={b.id} style={{ background: opsColors.bgPrimary }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                  <span style={{ fontWeight: 700, color: opsColors.textPrimary }}>{b.name}</span>
                  <OpsButton variant="danger" size="sm" onClick={() => setBindings(bindings.filter((_, j) => j !== i))}>Remove</OpsButton>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem' }}>
                  <div>
                    <FormLabel>Parallelism Target</FormLabel>
                    <FormInput type="number" value={b.parallelism} onChange={(e) => {
                      const updated = [...bindings]
                      updated[i] = { ...updated[i], parallelism: Number(e.target.value) }
                      setBindings(updated)
                    }} />
                  </div>
                  <div>
                    <FormLabel>Weight/Priority</FormLabel>
                    <FormInput type="number" value={b.priorityVal} onChange={(e) => {
                      const updated = [...bindings]
                      updated[i] = { ...updated[i], priorityVal: Number(e.target.value) }
                      setBindings(updated)
                    }} />
                  </div>
                  <div>
                    <FormLabel>Degrade Rule</FormLabel>
                    <FormSelect value={b.degradeRule} onChange={(e) => {
                      const updated = [...bindings]
                      updated[i] = { ...updated[i], degradeRule: e.target.value as DegradeRule }
                      setBindings(updated)
                    }}>
                      <option value="Pause">Pause</option>
                      <option value="Reduce">Reduce</option>
                      <option value="Skip">Skip</option>
                      <option value="Alert Only">Alert Only</option>
                    </FormSelect>
                  </div>
                </div>
              </OpsCard>
            ))}
          </FormSection>
        )}

        {/* Step 4: Horizon Config */}
        {step === 3 && (
          <FormSection>
            <OpsCardHeader><h3>Rolling Horizon Configuration</h3></OpsCardHeader>
            <div>
              <FormLabel>Horizon Type</FormLabel>
              <RadioGroup>
                <RadioCard active={horizonType === 'Next Hours'} onClick={() => setHorizonType('Next Hours')}>Next Hours</RadioCard>
                <RadioCard active={horizonType === 'Next Shift'} onClick={() => setHorizonType('Next Shift')}>Next Shift</RadioCard>
              </RadioGroup>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem' }}>
              <div>
                <FormLabel>Horizon Value</FormLabel>
                <FormInput type="number" value={horizonValue} onChange={(e) => setHorizonValue(e.target.value)} />
              </div>
              <div>
                <FormLabel>Replenish Threshold</FormLabel>
                <FormInput type="number" value={replenishThreshold} onChange={(e) => setReplenishThreshold(e.target.value)} />
              </div>
              <div>
                <FormLabel>Max Queue Size</FormLabel>
                <FormInput type="number" value={maxQueueSize} onChange={(e) => setMaxQueueSize(e.target.value)} />
              </div>
            </div>
            <InfoBox variant="info">
              The scheduler will look {horizonValue} {horizonType === 'Next Hours' ? 'hours' : 'shifts'} ahead.
              When the queue drops below {replenishThreshold} tasks, new work will be generated (max {maxQueueSize} total).
            </InfoBox>
          </FormSection>
        )}

        {/* Step 5: Review */}
        {step === 4 && (
          <FormSection>
            <OpsCardHeader><h3>Review & Create</h3></OpsCardHeader>
            <ReviewSection>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <ReviewLabel>Plan Identity</ReviewLabel>
                <span style={{ fontSize: '12px', color: opsColors.brand500, cursor: 'pointer' }} onClick={() => setStep(0)}>Edit</span>
              </div>
              <ReviewValue>{planName || 'Not set'}</ReviewValue>
              <div style={{ fontSize: '12px', color: opsColors.textFaded }}>Priority: {priority}</div>
            </ReviewSection>
            <ReviewSection>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <ReviewLabel>Target</ReviewLabel>
                <span style={{ fontSize: '12px', color: opsColors.brand500, cursor: 'pointer' }} onClick={() => setStep(1)}>Edit</span>
              </div>
              <ReviewValue>{targetValue} {METRIC_UNITS[metricType]} {dueDate ? `by ${dueDate}` : '(continuous)'}</ReviewValue>
            </ReviewSection>
            <ReviewSection>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <ReviewLabel>Cycle Templates</ReviewLabel>
                <span style={{ fontSize: '12px', color: opsColors.brand500, cursor: 'pointer' }} onClick={() => setStep(2)}>Edit</span>
              </div>
              <ReviewValue>{bindings.length} template{bindings.length !== 1 ? 's' : ''} bound</ReviewValue>
            </ReviewSection>
            <ReviewSection>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <ReviewLabel>Horizon</ReviewLabel>
                <span style={{ fontSize: '12px', color: opsColors.brand500, cursor: 'pointer' }} onClick={() => setStep(3)}>Edit</span>
              </div>
              <ReviewValue>{horizonType}: {horizonValue} | Threshold: {replenishThreshold} | Max: {maxQueueSize}</ReviewValue>
            </ReviewSection>
            <InfoBox variant="warning">
              Plan will be created as DRAFT. No tasks will be generated until activation.
            </InfoBox>
            <div style={{ fontSize: '12px', color: opsColors.textFaded }}>
              Provenance: source: PLAN | generatedAt: {new Date().toISOString()}
            </div>
          </FormSection>
        )}

        {/* Navigation buttons */}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '1.5rem' }}>
          <OpsButton variant="secondary" onClick={() => step > 0 ? setStep(step - 1) : navigate('/ops-control/plans')}>
            {step > 0 ? 'Previous' : 'Cancel'}
          </OpsButton>
          {step < STEPS.length - 1 ? (
            <OpsButton onClick={() => setStep(step + 1)}>Next</OpsButton>
          ) : (
            <OpsButton onClick={handleCreate}>Create Plan</OpsButton>
          )}
        </div>
      </OpsCard>
    </OpsPageWrapper>
  )
}
